import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { enquiriesAPI } from '../../utils/api';
import { 
  FaArrowLeft, FaUser, FaEnvelope, FaPhone, FaBriefcase, 
  FaCalendarAlt, FaPaperclip, FaHistory, FaCopy, FaPaperPlane, FaTrash,
  FaCreditCard
} from 'react-icons/fa';
import '../../styles/AdminEnquiryDetail.scss';

const AdminEnquiryDetail = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Status dropdown state
  const [status, setStatus] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Email form state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const fileInputRef = useRef(null);

  // Email history state
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch Enquiry details and Email History
  useEffect(() => {
    fetchDetail();
    fetchEmailHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);

  const showToast = (msg, toastType = 'success') => {
    setToast({ show: true, message: msg, type: toastType });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = type === 'mba'
        ? await enquiriesAPI.getMbaEnquiryById(id)
        : await enquiriesAPI.getSoftwareEnquiryById(id);
      
      const responseData = response.data || response;
      if (responseData.success && responseData.data) {
        setEnquiry(responseData.data);
        setStatus(responseData.data.status);
        // If opened for first time (status was 'New' and updated to 'Open' by API)
        if (responseData.autoOpened) {
          showToast('Status Updated To Open', 'info');
        }
      } else {
        throw new Error('Enquiry not found');
      }
    } catch (err) {
      setError('Failed to load enquiry details');
      console.error('Error fetching detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = type === 'mba'
        ? await enquiriesAPI.getMbaEmailHistory(id)
        : await enquiriesAPI.getSoftwareEmailHistory(id);
      
      const responseData = response.data || response;
      if (responseData.success && Array.isArray(responseData.data)) {
        setEmailHistory(responseData.data);
      }
    } catch (err) {
      console.error('Error fetching email history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setStatusUpdating(true);
      const response = type === 'mba'
        ? await enquiriesAPI.updateMbaStatus(id, newStatus)
        : await enquiriesAPI.updateSoftwareStatus(id, newStatus);
      
      const responseData = response.data || response;
      if (responseData.success) {
        setStatus(newStatus);
        setEnquiry(prev => ({ ...prev, status: newStatus }));
        showToast('Status Updated Successfully', 'success');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      showToast('Failed to update status', 'error');
      console.error('Status update error:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleArchive = async () => {
    if (window.confirm('Are you sure you want to archive (soft delete) this lead?')) {
      try {
        const response = await enquiriesAPI.archive(id);
        const responseData = response.data || response;
        if (responseData.success) {
          showToast('Enquiry archived successfully', 'success');
          setTimeout(() => {
            navigate(`/admin/${type}-enquiries`);
          }, 1500);
        } else {
          throw new Error('Archive failed');
        }
      } catch (err) {
        showToast('Failed to archive enquiry', 'error');
        console.error('Archive error:', err);
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to permanently delete this enquiry? This action cannot be undone.')) {
      try {
        const response = await enquiriesAPI.delete(id);
        const responseData = response.data || response;
        if (responseData.success) {
          showToast('Enquiry permanently deleted', 'success');
          setTimeout(() => {
            navigate(`/admin/${type}-enquiries`);
          }, 1500);
        } else {
          throw new Error('Deletion failed');
        }
      } catch (err) {
        showToast('Failed to delete enquiry', 'error');
        console.error('Delete error:', err);
      }
    }
  };

  const handleTogglePaymentStatus = async () => {
    try {
      const nextStatus = enquiry.paymentStatus === 'Paid' ? 'Pending' : 'Paid';
      if (!window.confirm(`Are you sure you want to mark this enquiry as ${nextStatus}?`)) {
        return;
      }
      setStatusUpdating(true);
      const response = await enquiriesAPI.updatePaymentStatus(id, nextStatus);
      const responseData = response.data || response;
      if (responseData.success) {
        setEnquiry(prev => ({ ...prev, paymentStatus: nextStatus }));
        showToast(`Payment marked as ${nextStatus} successfully`, 'success');
      }
    } catch (err) {
      showToast('Failed to update payment status', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleResendPaymentEmail = async () => {
    try {
      if (!window.confirm('Are you sure you want to resend the payment instruction email?')) {
        return;
      }
      setStatusUpdating(true);
      const response = await enquiriesAPI.resendPaymentEmail(id);
      const responseData = response.data || response;
      if (responseData.success) {
        showToast('Payment instruction email resent successfully!', 'success');
      }
    } catch (err) {
      showToast('Failed to resend payment email', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleUploadProof = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setStatusUpdating(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await enquiriesAPI.uploadPaymentProof(id, formData);
      const responseData = response.data || response;
      if (responseData.success && responseData.data) {
        setEnquiry(prev => ({ ...prev, paymentProof: responseData.data.payment_proof }));
        showToast('Payment proof uploaded successfully!', 'success');
      }
    } catch (err) {
      showToast('Failed to upload payment proof', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDownloadProof = async () => {
    try {
      const response = await enquiriesAPI.downloadPaymentProof(id);
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data || response]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proof_${enquiry.enquiryId || id}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast('Failed to download proof', 'error');
      console.error(err);
    }
  };

  // Copy helpers
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard!`, 'success');
  };

  // Email composer attachment handlers
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    validateAndAddFiles(files);
  };

  const validateAndAddFiles = (files) => {
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.zip', '.png', '.jpg', '.jpeg'];
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    const validFiles = [];

    for (let f of files) {
      const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        showToast(`File type '${ext}' not allowed. Only PDF, DOC, DOCX, ZIP, and images (PNG, JPG) permitted.`, 'error');
        continue;
      }
      if (f.size > maxSizeBytes) {
        showToast(`File '${f.name}' exceeds the 10MB maximum size limit.`, 'error');
        continue;
      }
      validFiles.push(f);
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      showToast('Subject and Message are required fields.', 'error');
      return;
    }

    try {
      setSendingEmail(true);
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('message', message);
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = type === 'mba'
        ? await enquiriesAPI.sendMbaEmail(id, formData)
        : await enquiriesAPI.sendSoftwareEmail(id, formData);
      
      const responseData = response.data || response;
      if (responseData.success) {
        showToast('Email Sent Successfully', 'success');
        setSubject('');
        setMessage('');
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchEmailHistory(); // Refresh history log
      } else {
        throw new Error(responseData.message || 'Email delivery failed');
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed To Send Email', 'error');
      console.error('Email send error:', err);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleClearEmailForm = () => {
    setSubject('');
    setMessage('');
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getStatusColor = (statusName) => {
    const colors = {
      New: '#FFA500',
      Open: '#4318ff',
      Pending: '#4169E1',
      Completed: '#01b574',
    };
    return colors[statusName] || '#808080';
  };

  if (loading) {
    return (
      <div className="crm-detail-page loading-state">
        <p>Loading enquiry details, please wait...</p>
      </div>
    );
  }

  if (error || !enquiry) {
    return (
      <div className="crm-detail-page error-state">
        <button className="back-btn" onClick={() => navigate(`/admin/${type}-enquiries`)}>
          <FaArrowLeft /> Back to Enquiries
        </button>
        <div className="error-message-box">{error || 'Enquiry details could not be found.'}</div>
      </div>
    );
  }

  return (
    <div className="crm-detail-page">
      {/* Toast Alert */}
      {toast.show && (
        <div className={`crm-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Header section */}
      <div className="crm-header">
        <button className="back-btn" onClick={() => navigate(`/admin/${type}-enquiries`)}>
          <FaArrowLeft /> Back to Enquiries
        </button>
        <div className="title-row">
          <h1>{type === 'mba' ? 'MBA Enquiry Details' : 'Software Enquiry Details'}</h1>
          <span 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(status) }}
          >
            {status}
          </span>
        </div>
      </div>

      <div className="crm-content-grid">
        {/* Left Column: Full Information Cards */}
        <div className="crm-info-column">
          {/* Card 1: Lead Status & Workflow management */}
          <div className="crm-card status-management-card">
            <h3>Workflow Control</h3>
            <div className="status-dropdown-group">
              <label>Update Lead Status:</label>
              <select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={statusUpdating}
                className="status-select"
              >
                <option value="New">🆕 New</option>
                <option value="Open">📂 Open</option>
                <option value="Pending">🕒 Pending</option>
                <option value="Completed">✔️ Completed</option>
              </select>
            </div>
          </div>

          {/* Card 1.5: Payment Details & Control */}
          <div className="crm-card payment-management-card" style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <FaCreditCard style={{ color: '#0ea5e9', fontSize: '20px' }} />
              <h3 style={{ margin: 0 }}>Payment Information</h3>
            </div>
            
            <div className="info-fields">
              <div className="info-field" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #edf2f7', paddingBottom: '8px' }}>
                <span className="field-label" style={{ color: '#718096', fontSize: '13px' }}>Enquiry ID:</span>
                <span className="field-value" style={{ fontWeight: 600, color: '#2d3748' }}>{enquiry.enquiryId || `PI-${enquiry.id}`}</span>
              </div>
              <div className="info-field" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #edf2f7', paddingBottom: '8px' }}>
                <span className="field-label" style={{ color: '#718096', fontSize: '13px' }}>Project Category:</span>
                <span className="badge" style={{ 
                  backgroundColor: enquiry.projectCategory === 'Software' ? '#ebf8ff' : '#feebc8', 
                  color: enquiry.projectCategory === 'Software' ? '#2b6cb0' : '#c05621',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {enquiry.projectCategory || 'MBA'}
                </span>
              </div>
              <div className="info-field" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #edf2f7', paddingBottom: '8px' }}>
                <span className="field-label" style={{ color: '#718096', fontSize: '13px' }}>Project Price:</span>
                <span className="field-value" style={{ fontWeight: 700, color: '#2d3748' }}>₹{(enquiry.projectPrice || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="info-field" style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #edf2f7', paddingBottom: '8px', alignItems: 'center' }}>
                <span className="field-label" style={{ color: '#718096', fontSize: '13px' }}>Payment Status:</span>
                <span className="badge" style={{ 
                  backgroundColor: enquiry.paymentStatus === 'Paid' ? '#01b574' : '#FFA500', 
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {enquiry.paymentStatus || 'Pending'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
              <button 
                type="button"
                onClick={handleTogglePaymentStatus}
                disabled={statusUpdating}
                className="action-btn"
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  backgroundColor: enquiry.paymentStatus === 'Paid' ? '#ecc94b' : '#48bb78', 
                  color: enquiry.paymentStatus === 'Paid' ? '#744210' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
              >
                {enquiry.paymentStatus === 'Paid' ? '🟡 Mark As Pending' : '🟢 Mark As Paid & Send Email'}
              </button>

              <button 
                type="button"
                onClick={handleResendPaymentEmail}
                disabled={statusUpdating}
                className="action-btn"
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  backgroundColor: '#4a5568', 
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
              >
                ✉ Resend Payment Instructions
              </button>

              {enquiry.paymentProof ? (
                <button 
                  type="button"
                  onClick={handleDownloadProof}
                  className="action-btn"
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    backgroundColor: '#3182ce', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.2s'
                  }}
                >
                  📥 Download Payment Proof
                </button>
              ) : (
                <div style={{ width: '100%' }}>
                  <label 
                    htmlFor="detail-proof-upload"
                    style={{ 
                      display: 'block', 
                      padding: '10px', 
                      backgroundColor: '#edf2f7', 
                      color: '#4a5568', 
                      border: '1px solid #cbd5e0',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                  >
                    📁 Upload Payment Proof
                  </label>
                  <input 
                    type="file" 
                    id="detail-proof-upload" 
                    style={{ display: 'none' }} 
                    onChange={handleUploadProof}
                    disabled={statusUpdating}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Card 2: Personal Information */}
          <div className="crm-card info-card">
            <div className="card-header-icon"><FaUser /></div>
            <h2>Personal Information</h2>
            <div className="info-fields">
              <div className="info-field">
                <span className="field-label">Student Name</span>
                <span className="field-value">{enquiry.name}</span>
              </div>
              <div className="info-field">
                <span className="field-label">Email Address</span>
                <span className="field-value"><a href={`mailto:${enquiry.email}`}>{enquiry.email}</a></span>
              </div>
              <div className="info-field">
                <span className="field-label">Mobile Number</span>
                <span className="field-value"><a href={`tel:${enquiry.mobile}`}>{enquiry.mobile}</a></span>
              </div>
              {(enquiry.college || enquiry.university) && (
                <div className="info-field">
                  <span className="field-label">Institution</span>
                  <span className="field-value">
                    {enquiry.college || '—'} {enquiry.university ? `(${enquiry.university})` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Project Information */}
          <div className="crm-card info-card">
            <div className="card-header-icon"><FaBriefcase /></div>
            <h2>Project Details</h2>
            <div className="info-fields">
              <div className="info-field">
                <span className="field-label">Project Title</span>
                <span className="field-value title-value">{enquiry.projectTitle}</span>
              </div>
              <div className="info-field">
                <span className="field-label">Specialization</span>
                <span className="field-value">{enquiry.specialization.replace(/^(MBA - |Software - )/, '')}</span>
              </div>
              <div className="info-field">
                <span className="field-label">Student Message / Description</span>
                <p className="description-value">{enquiry.description || enquiry.message || 'No custom message.'}</p>
              </div>
            </div>
          </div>

          {/* Card 4: Submission Meta Information */}
          <div className="crm-card info-card">
            <div className="card-header-icon"><FaCalendarAlt /></div>
            <h2>Submission Info</h2>
            <div className="info-fields">
              <div className="info-field">
                <span className="field-label">Submitted At</span>
                <span className="field-value">
                  {new Date(enquiry.createdAt || enquiry.created_at).toLocaleString()}
                </span>
              </div>
              {enquiry.completedAt && (
                <div className="info-field">
                  <span className="field-label">Completed At</span>
                  <span className="field-value font-success">
                    {new Date(enquiry.completedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Communication & Quick Actions */}
        <div className="crm-actions-column">
          {/* Card 1: Quick Actions */}
          <div className="crm-card quick-actions-card">
            <h2>Quick Actions</h2>
            <div className="actions-buttons-grid">
              <a href={`tel:${enquiry.mobile}`} className="action-btn call-btn">
                <FaPhone /> Call User
              </a>
              <a href={`mailto:${enquiry.email}`} className="action-btn email-btn">
                <FaEnvelope /> Email User
              </a>
              <button 
                onClick={() => copyToClipboard(enquiry.mobile, 'Mobile number')} 
                className="action-btn copy-btn"
              >
                <FaCopy /> Copy Mobile
              </button>
              <button 
                onClick={() => copyToClipboard(enquiry.email, 'Email address')} 
                className="action-btn copy-btn"
              >
                <FaCopy /> Copy Email
              </button>
              <button onClick={handleArchive} className="action-btn archive-btn">
                <FaTrash /> Archive Lead
              </button>
              <button onClick={handleDelete} className="action-btn delete-btn">
                <FaTrash /> Delete Lead
              </button>
            </div>
          </div>

          {/* Card 2: Send Email Composer */}
          <div className="crm-card email-composer-card">
            <h2>Send Email To User</h2>
            <form onSubmit={handleSendEmail} className="email-form">
              <div className="form-group">
                <label>To:</label>
                <input type="text" value={enquiry.email} readOnly className="readonly-input" />
              </div>
              <div className="form-group">
                <label>Subject:</label>
                <input 
                  type="text" 
                  value={subject} 
                  onChange={(e) => setSubject(e.target.value)} 
                  placeholder="Enter email subject"
                  required 
                />
              </div>
              <div className="form-group">
                <label>Message:</label>
                <textarea 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)} 
                  placeholder="Compose your reply details here..."
                  rows="6"
                  required 
                />
              </div>
              
              <div className="form-group">
                <label>Attachments:</label>
                <div className="attachment-upload-area">
                  <input 
                    type="file" 
                    multiple 
                    ref={fileInputRef}
                    onChange={handleFileChange} 
                    id="attachment-input"
                    className="attachment-file-input"
                  />
                  <label htmlFor="attachment-input" className="upload-label">
                    <FaPaperclip /> Click to select attachments
                  </label>
                  <span className="file-rules">Allowed: PDF, DOC, DOCX, ZIP, PNG, JPG (Max 10MB per file)</span>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="attached-files-list">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="attached-file-item">
                        <span className="file-name">{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        <button type="button" onClick={() => removeFile(idx)} className="btn-remove-file">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="composer-actions">
                <button type="submit" disabled={sendingEmail} className="btn-send">
                  <FaPaperPlane /> {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
                <button type="button" onClick={handleClearEmailForm} className="btn-clear">
                  Clear
                </button>
              </div>
            </form>
          </div>

          {/* Card 3: Email History List */}
          <div className="crm-card email-history-card">
            <h2><FaHistory /> Communication History</h2>
            {loadingHistory ? (
              <p className="loading-history">Loading history log...</p>
            ) : emailHistory.length === 0 ? (
              <p className="empty-history">No communication emails sent yet.</p>
            ) : (
              <div className="history-list">
                {emailHistory.map((item) => {
                  let attachmentsList = [];
                  try {
                    if (item.attachments) {
                      attachmentsList = typeof item.attachments === 'string' 
                        ? JSON.parse(item.attachments) 
                        : item.attachments;
                    }
                  } catch (e) {
                    console.error('Error parsing attachments', e);
                  }
                  
                  return (
                    <div key={item.id} className="history-item">
                      <div className="history-meta">
                        <span className="sent-date">{new Date(item.sentAt || item.sent_at).toLocaleString()}</span>
                        <span className="sent-by">Sent by: {item.sentBy || 'Admin'}</span>
                      </div>
                      <h4 className="history-subject">{item.subject}</h4>
                      <p className="history-body-preview">{item.message}</p>
                      
                      {attachmentsList.length > 0 && (
                        <div className="history-attachments">
                          <span className="attach-label"><FaPaperclip /> Attachments:</span>
                          <div className="attach-links">
                            {attachmentsList.map((file, fIdx) => (
                              <a 
                                key={fIdx}
                                href={`${process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8000'}/api/admin/downloads/attachments/${file.saved_name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="history-attach-link"
                              >
                                {file.filename}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEnquiryDetail;
